<template>
	<div class="search-bar__wrapper" :class="theme">
		<form class="search-bar-form" @submit.prevent="submitSearch">
			<label class="search-icon" for="searchbar-input">
				<FontAwesomeIcon class="icon" :icon="faSearch" />
			</label>

			<input
				id="searchbar-input"
				class="search-bar__input"
				type="search"
				ref="searchBarInput"
				autocomplete="off"
				:placeholder="placeholder"
				:value="model"
				@input="updateSearchQuery"
				@focus="$emit('focus')"
				@keyup.down="$emit('arrow-down')"
			/>
		</form>

		<div v-if="clearable && model" class="search-bar__clear">
			<FontAwesomeIcon class="icon" :icon="faTimes" @click.stop.prevent="clearSearch" />
		</div>
	</div>
</template>

<script setup lang="ts">
import dompurify from 'dompurify';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ref } from 'vue';

const model = defineModel<string>({ default: '' });

withDefaults(
	defineProps<{
		placeholder: string;
		theme?: 'dark' | 'light';
		clearable?: boolean;
	}>(),
	{
		theme: 'dark',
		clearable: true,
	}
);

const emit = defineEmits<{
	(e: 'submit'): void;
	(e: 'focus'): void;
	(e: 'arrow-down'): void;
}>();

// Used for keyboard navigation in SearchSuggesterBar.vue
const searchBarInput = ref<HTMLInputElement | null>(null);
defineExpose({ searchBarInput });

function updateSearchQuery(event: Event) {
	model.value = dompurify.sanitize((event.target as HTMLInputElement).value.trim());
}

function clearSearch() {
	model.value = '';
}

function submitSearch() {
	emit('submit');
	searchBarInput.value?.blur();
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.search-bar__wrapper {
	display: flex;
	width: 100%;
	min-width: 0;
	min-height: 35px;
	max-height: 35px;
	align-items: center;
	padding: 5px;
	margin: 10px 0;
	border-radius: 4px;

	&.dark {
		background: $jw-color-darkgray--dark;

		.search-bar__input {
			color: #ccc;
		}
	}

	&.light {
		background: $jw-color-darkgray;

		.search-bar__input {
			color: $jw-color-white;
		}
	}

	.search-bar-form {
		display: flex;
		align-items: center;
		width: 100%;
		padding-inline: 5px;
		gap: 5px;

		.search-icon {
			color: #666666;
		}

		.search-bar__input {
			background: none;
			border: none;
			outline: none;
			font-size: 16px;
			width: 100%;
			overflow-anchor: none;

			&:focus {
				outline: none;
				color: #eee;
			}
		}
	}

	.search-bar__input::-webkit-search-cancel-button {
		display: none;
	}

	.search-bar__clear {
		display: flex;
		align-items: center;
		margin: 0 5px;
		cursor: pointer;
	}
}
</style>
